import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { auth } from './firebase';
import { ImpersonationProvider } from './contexts/ImpersonationContext';
import ImpersonationBanner from './components/admin/ImpersonationBanner';
import Impersonation from './components/admin/Impersonation';
import ImpersonationAuditList from './components/admin/ImpersonationAuditList';
import ImpersonationAuditDetail from './components/admin/ImpersonationAuditDetail';
import LandingPage from './components/LandingPage';
import Login from './components/Login';
import Constructor from './components/Constructor';
import Dashboard from './components/Dashboard';
import Register from './components/Register.jsx';
import PrivateRoute from './PrivateRoute';
import Layout from './components/Layout';
import WhatsappChatbotInfo from './components/WhatsappChatbotInfo';
import WebsiteChatbotInfo from './components/WebsiteChatbotInfo';
import ImageUpload from './components/ImageUpload';
import Analytics from './components/Analytics';
import EMRDashboard from './components/emr/EMRDashboard';
import AgendaCitasPage from './components/emr/AgendaCitasPage';
import ConsumoTokens from './components/ConsumoTokens';
import Suscripcion from './components/Suscripcion';
import { SmartFlowsList, SmartFlowEditor } from './components/SmartFlowBuilder';
import AdminPlanes from './components/AdminPlanes';
import AdminServices from './components/AdminServices';
import AdminPoliticas from './components/AdminPoliticas';
import AdminTests from './components/AdminTests';
import AdminCatalogoEstudios from './components/AdminCatalogoEstudios';
import AdminVademecum from './components/AdminVademecum';
import AdminCorrections from './components/AdminCorrections';
import AdminDB from './components/AdminDB';
import DesignStudio from './components/DesignStudio';
import ClassifierAdmin from './components/ClassifierAdmin';
import ConversacionesDashboard from './components/ConversacionesDashboard';
import Finanzas from './components/Finanzas';
import CatalogoServicios from './components/CatalogoServicios';
import NaraChat from './components/NaraChat';
import HubAuth from './components/HubAuth';
import PlantillasVoz from './components/PlantillasVoz';
import Inbox from './components/Inbox';
import AdminPlantillasChatbot from './components/AdminPlantillasChatbot';
import './App.css';

// Oculta el warning de ResizeObserver en todos los entornos
window.addEventListener('error', function(e) {
  if (e.message && e.message.includes('ResizeObserver loop completed')) {
    e.preventDefault();
  }
});


function App() {
  // Mantenemos el firebase UID actualizado para que el ImpersonationProvider
  // pueda hacer requests a /admin/impersonate/* con el header X-User-Id.
  const [firebaseUid, setFirebaseUid] = useState(() => auth.currentUser?.uid || null);
  useEffect(() => {
    return auth.onAuthStateChanged((u) => setFirebaseUid(u?.uid || null));
  }, []);

  return (
    <Router>
      <ImpersonationProvider userId={firebaseUid}>
        <ImpersonationBanner />
      <Routes>
        <Route path="/hub-auth" element={<HubAuth />} />
        <Route path="/" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<PrivateRoute><Layout><Dashboard /></Layout></PrivateRoute>} />
        <Route path="/constructor" element={<PrivateRoute><Layout><Constructor /></Layout></PrivateRoute>} />
        <Route path="/analytics" element={<PrivateRoute><Layout><Analytics /></Layout></PrivateRoute>} />
        <Route path="/emr/*" element={<PrivateRoute><Layout><EMRDashboard /></Layout></PrivateRoute>} />
        <Route path="/agenda" element={<PrivateRoute><Layout><AgendaCitasPage /></Layout></PrivateRoute>} />
        <Route path="/whatsapp" element={<Navigate to="/analytics?tab=whatsapp" replace />} />
        <Route path="/consumo-tokens" element={<PrivateRoute><Layout><ConsumoTokens /></Layout></PrivateRoute>} />
        <Route path="/suscripcion" element={<PrivateRoute><Layout><Suscripcion /></Layout></PrivateRoute>} />
        <Route path="/smart-flows" element={<PrivateRoute><Layout><SmartFlowsList /></Layout></PrivateRoute>} />
        <Route path="/smart-flows/:chatbotId" element={<PrivateRoute><Layout><SmartFlowEditor /></Layout></PrivateRoute>} />
        <Route path="/admin/planes" element={<PrivateRoute><Layout><AdminPlanes /></Layout></PrivateRoute>} />
        <Route path="/admin/servicios" element={<PrivateRoute><Layout><AdminServices /></Layout></PrivateRoute>} />
        <Route path="/admin/politicas" element={<PrivateRoute><Layout><AdminPoliticas /></Layout></PrivateRoute>} />
        <Route path="/admin/tests" element={<PrivateRoute><Layout><AdminTests /></Layout></PrivateRoute>} />
        <Route path="/admin/catalogo-estudios" element={<PrivateRoute><Layout><AdminCatalogoEstudios /></Layout></PrivateRoute>} />
        <Route path="/admin/catalogo-medicamentos" element={<PrivateRoute><Layout><AdminVademecum /></Layout></PrivateRoute>} />
        <Route path="/admin/corrections" element={<PrivateRoute><Layout><AdminCorrections /></Layout></PrivateRoute>} />
        <Route path="/admin/db" element={<PrivateRoute><Layout><AdminDB /></Layout></PrivateRoute>} />
        <Route path="/admin/design-studio" element={<PrivateRoute><Layout><DesignStudio /></Layout></PrivateRoute>} />
        <Route path="/admin/clasificador" element={<PrivateRoute><Layout><ClassifierAdmin /></Layout></PrivateRoute>} />
        <Route path="/admin/impersonate" element={<PrivateRoute><Layout><Impersonation /></Layout></PrivateRoute>} />
        <Route path="/admin/impersonate/audit" element={<PrivateRoute><Layout><ImpersonationAuditList /></Layout></PrivateRoute>} />
        <Route path="/admin/impersonate/audit/:sessionId" element={<PrivateRoute><Layout><ImpersonationAuditDetail /></Layout></PrivateRoute>} />
        <Route path="/conversaciones" element={<PrivateRoute><Layout><ConversacionesDashboard /></Layout></PrivateRoute>} />
        <Route path="/finanzas" element={<PrivateRoute><Layout><Finanzas /></Layout></PrivateRoute>} />
        <Route path="/catalogo" element={<PrivateRoute><Layout><CatalogoServicios /></Layout></PrivateRoute>} />
        <Route path="/plantillas-voz" element={<PrivateRoute><Layout><PlantillasVoz /></Layout></PrivateRoute>} />
        <Route path="/inbox" element={<PrivateRoute><Layout><Inbox /></Layout></PrivateRoute>} />
        <Route path="/admin/catalogo-plantillas" element={<PrivateRoute><Layout><AdminPlantillasChatbot /></Layout></PrivateRoute>} />
        <Route path="/whatsapp-chatbot" element={<WhatsappChatbotInfo />} />
        <Route path="/website-chatbot" element={<WebsiteChatbotInfo />} />
        <Route path="/subir-imagen/:token" element={<ImageUpload />} />
        <Route path="*" element={<Login />} />
      </Routes>
      <NaraChat />
      </ImpersonationProvider>
    </Router>
  );
}

export default App;
